import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from "lucide-react";


const Products = () => {
  const products = [
    {
      name: "H-Square",
      tagline: "Highlighting Hub — UAN, IP & Bank Statement",
      desc: "Enterprise-grade compliance data processing engine for organisations managing large volumes of data across multiple locations. Real-time dashboards with simultaneous processing and proactive validation.",
      features: [
        "Multi-code simultaneous processing — hundreds of locations",
        "Real-time dashboards — by state, entity, or compliance type",
        "Data validation engine — flags anomalies before submissions",
        "Enterprise-grade AES data encryption",
        "Role-based access for compliance, HR, and finance teams",
        "Integration APIs for HRMS and payroll systems",
        "Flexible pay-per-use pricing model",
      ],
      outcomes: [
        { num: "100%", label: "Reduction in compliance team workload" },
        { num: "0%", label: "Errors in data processing" },
        { num: "100%", label: "Real-time visibility across all locations" },
        { num: "25 Mins", label: "Audit-ready reports, not days" },
      ],
      dashboard: [
        { label: "UAN Records Processed", value: "60000", width: "98%" },
        { label: "IP Records Processed", value: "45000", width: "88%" },
        { label: "Bank Records Processed", value: "35000", width: "79%" },
      ],
    },
    {
      name: "R-Cube",
      tagline: "Registers, Records & Returns",
      desc: "Enterprise-grade compliance data processing engine for organisations managing large volumes of data across multiple locations. Real-time dashboards with simultaneous processing and proactive validation.",
      features: [
        "Bulk payroll upload & reconciliation — multi-entity",
        "Automated registers — Factories Act, Shops Act, CLRA",
        "Central repository for all registers, records, returns",
        "Smart alerts for due dates, renewals, and deadlines",
        "Audit trail — version history and change logs",
        "Scalable subscription pricing for SMEs to enterprises",
      ],
      outcomes: [
        { num: "100%", label: "Reduction in compliance team workload" },
        { num: "0", label: "Manual errors in data processing" },
        { num: "100%", label: "Real-time visibility across all locations" },
        { num: "40 Mins", label: "Audit-ready reports, not days" },
      ],
      dashboard: [
        { label: "Site Handled Monthly", value: "2000+", width: "60%" },
        { label: "Employees Handled Monthly", value: "60000", width: "88%" },
        { label: "CLRA Registers Prepared", value: "1,00,000+", width: "99%" },
        { label: "CLRA Returns Prepared", value: "500+", width: "55%" },
      ],
    },
  ];
  const navigate = useNavigate();
const handleFreeConsultation = () => navigate('/contact');


  return (
   <div className="bg-white pt-10">
      <div className="max-w-7xl mx-auto space-y-16">

        {products.map((product, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02, y: -5 }}
            transition={{ duration: 0.5 }}
            className="border border-[#d97925]/40 rounded-3xl p-8 md:p-12 shadow-md hover:shadow-[0_0_30px_rgba(217,121,37,0.3)] transition-all duration-300 bg-white"
          >
            <div className="grid md:grid-cols-2 gap-12 items-start">

              {/* LEFT CONTENT */}
              <div>
                <span className="text-xs bg-green-100 text-green-600 px-3 py-1 rounded-full mb-4 inline-block">
                  ● LIVE NOW
                </span>

                <h1 className="text-4xl font-bold text-black mb-2">
                  {product.name}
                </h1>

                <p className="text-sm text-gray-500 uppercase mb-4">
                  {product.tagline}
                </p>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {product.desc}
                </p>

                {/* FEATURES */}
                <div className="space-y-2 mb-8">
                  {product.features.map((item, idx) => (
                    <div key={idx} className="flex gap-2 text-gray-700">
                      <span className="text-[#d97925]">✓</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* OUTCOMES */}
                <div className="grid grid-cols-2 gap-4">
                  {product.outcomes.map((item, idx) => (
                    <div
                      key={idx}
                      className="border border-[#d97925]/30 rounded-xl p-4"
                    >
                      <div className="text-xl font-bold text-[#d97925]">
                        {item.num}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT DASHBOARD */}
              <div className="bg-[#1a1a1a] border border-gray-700 rounded-2xl p-6 shadow-inner">
                <h3 className="text-gray-400 text-sm mb-4 uppercase tracking-wider">
                  {product.name} Live Dashboard
                </h3>

                {product.dashboard.map((item, idx) => (
                  <div key={idx} className="mb-5">
                    <div className="flex justify-between text-sm text-gray-300 mb-1">
                      <span>{item.label}</span>
                      <span className="text-[#d97925] font-semibold">
                        {item.value}
                      </span>
                    </div>

                    <div className="w-full h-2 bg-gray-700 rounded-full">
                      <div
                        className="h-2 bg-[#d97925] rounded-full"
                        style={{ width: item.width }}
                      />
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </motion.div>
        ))}

      </div>
       <section className="py-28  bg-gradient-to-br from-[#111111] to-[#111111] text-white">
               <div className="max-w-7xl mx-auto px-6 text-center">
                 <motion.h2
                   initial={{ opacity: 0, y: 60 }}
                   whileInView={{ opacity: 1, y: 0 }}
                   viewport={{ once: true }}
                   transition={{ duration: 0.9 }}
                   className="text-5xl font-bold tracking-tight mb-8"
                 >
                   Stay Compliant. Stay Ahead.
                 </motion.h2>
       
                 <p className="text-2xl mb-12 max-w-3xl mx-auto opacity-90">
                  Smart Resources that helps your business stay protected, prepared and compliant.
                 </p>
       
                 <motion.button
                   whileHover={{ scale: 1.05 }}
                   whileTap={{ scale: 0.98 }}
                   onClick={handleFreeConsultation}
                   className="inline-flex items-center gap-4 px-12 py-7 bg-white text-[#d97925] font-bold text-2xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-colors"
                 >
                   Request a Free Demo
                   <ArrowRight size={32} />
                 </motion.button>
               </div>
             </section>
    </div>
  );
};

export default Products;